// frontend/src/components/homepage/HeroLive.js
//
// The live-mesh hero: headline, sub, body plate, both CTAs, the micro line, the
// three stats, one lead card and the scanned-posts tray.
//
// IT IS ALIVE FROM P.4. The card fills, grows out of an ignited node in the
// mesh, counts its score, holds, files itself away and comes back as the next
// lead; the tray counts scanned posts and flies into the navbar when the hero
// scrolls past; a profession label appears wherever the graph ignites. Every one
// of those hangs off src/motion/, and every one of them stops dead when a
// visitor asks it to.
//
// THIS FILE IS THE COMPOSITION, not the mechanics. The cycle is useHeroCycle.js,
// the card is LeadCardLive.js, the counter and its dock are ScannedTray.js and
// the labels are IgniteTag.js - each one testable on its own.
//
// Everything that is not motion is declarative markup + the static classes in
// src/homepage/homepage.css, because MUI `sx` re-inserts an inline @keyframes on
// every render and Chrome restarts the animation when it does.
//
// SEO SHAPE, deliberately preserved from the current HeroSection:
//   * ONE <h1> for the whole slogan. The two lines used to be two literal <h1>
//     tags (fixed 2026-08-12, Bing hygiene round). Each line is a display:block
//     span inside the single h1, and each WORD is its own span so it can arrive
//     separately - with an explicit {" "} between words so a crawler reading
//     textContent does not concatenate them.
//   * Both CTAs are real anchors (RouterLink), never a clickable <div>.
//   * Every headline, stat, card line and label is real DOM text. Nothing that
//     matters is painted into a canvas, so the prerender snapshot carries it.

import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link as RouterLink } from "react-router-dom";
import { Box, Typography } from "@mui/material";
import SectionShell from "./SectionShell";
import LeadCardLive from "./LeadCardLive";
import HeroCarouselLive from "./HeroCarouselLive";
import ScannedTray from "./ScannedTray";
import IgniteTag from "./IgniteTag";
import Arrow from "./Arrow";
import useHeroCycle from "./useHeroCycle";
import { LEADS, FIELDS, HERO_HOT_SCORE } from "./heroData";
import useMotion from "../../motion/useMotion";
import { getMotionState } from "../../motion/motionSignal";
import useInViewGate, { isPrerendering } from "../../motion/useInViewGate";
import useHydrated from "../../motion/useHydrated";
import rafThrottle from "../../motion/rafThrottle";
// From the constants module, never from meshEngine: a static import of the
// engine here would pull it into the eager bundle and defeat the lazy split.
import { MOBILE_MAX_WIDTH } from "../../homepage/meshConstants";

// The hero's own breakpoint, and it is NOT one of MUI's. The design pins the
// two-column split and the "line 1 never wraps" rule to 1000px - between md
// (900) and lg (1200) - and the fit routine below measures against the same
// number. Snapping it to md or lg would either wrap the headline on a laptop or
// leave a 1100px window with a one-column hero and a nowrap headline.
const WIDE = "@media (min-width:1000px)";
const WIDE_MIN_PX = 1000;
// The phone scale (owner round 2): the same 900px the mesh's motion budget and
// homepage.css's `@media (max-width: 900px)` hero block key on, so the type,
// the plates and the spacing all change together at one width.
const MOBILE = `@media (max-width:${MOBILE_MAX_WIDTH}px)`;

/** How long a burst ring lives, matched to the hl-nodeBurst keyframe. */
const BURST_MS = 1100;

const isMobileWidth = () =>
  typeof window !== "undefined" && window.innerWidth < MOBILE_MAX_WIDTH;

const COPY = {
  he: {
    line1: ["מקסימום", "הזדמנויות."],
    line2: ["מינימום", "מאמץ."],
    sub: "ה-AI שלנו עושה הכל בשבילך.",
    primaryCta: "הצטרף",
    secondaryCta: "התחבר",
    micro: "מתחילים בחינם תוך 60 שניות • ללא כרטיס אשראי",
    stats: [
      { n: "+80h", l: "חיסכון חודשי" },
      { n: "24/7", l: "ניטור רציף" },
      // The FULL label at every width (owner, 2026-09-06). It is 19 Hebrew
      // characters where the equal-thirds row gave it 83px, so the room was
      // found rather than the words dropped: on phones the row bleeds 18px a
      // side AND its columns size to their content, which is what pays for
      // this label at full size. Measured at 360: 113px of chip in a 316px row
      // with 56px to spare. NumbersLive's tile keeps the longer "מדי חודש".
      { n: "500,000+", l: "פוסטים נסרקים בחודש" },
    ],
    scoreLabel: "ציון AI",
    hotStamp: "HOT LEAD",
    trayLabel: "פוסטים שנסרקו מאז שנכנסת לאתר",
    scroll: "SCROLL",
  },
  en: {
    line1: ["Maximum", "Opportunities."],
    line2: ["Minimum", "Effort."],
    sub: "Our AI Does It All For You.",
    primaryCta: "Sign Up",
    secondaryCta: "Login",
    micro: "Start free in 60 seconds • No credit card",
    stats: [
      { n: "+80h", l: "Monthly savings" },
      { n: "24/7", l: "Continuous monitoring" },
      { n: "500,000+", l: "Posts scanned monthly" },
    ],
    scoreLabel: "AI score",
    hotStamp: "HOT LEAD",
    trayLabel: "Posts scanned since you arrived",
    scroll: "SCROLL",
  },
};

/**
 * The body paragraph, the one piece of hero copy with inline emphasis.
 *
 * Three plates on a wide screen (one table-display box per clause, exactly the
 * prototype), ONE flowing plate on a phone: `.hl-hero-flow` is `display:
 * contents` above 900px and an inline box with `box-decoration-break: clone`
 * below it, so the glass hugs every wrapped line of a real paragraph instead
 * of stacking three ragged blocks. The explicit `{" "}` between the clauses is
 * what keeps them from running together once they are inline; between the
 * table boxes it is collapsed whitespace and renders nothing.
 */
const HeroBody = ({ isHebrew }) =>
  isHebrew ? (
    <span className="hl-hero-flow">
      <span className="hl-plate">Leadify AI מנטרת את השיח ברשתות החברתיות באופן רציף,</span>{" "}
      <span className="hl-plate">
        מאתרת עבורך את <strong>הזדמנויות הזהב</strong> מכל מקום בכל זמן
      </span>{" "}
      <span className="hl-plate">
        ושולחת לך התראות <strong>בזמן אמת</strong>
      </span>
    </span>
  ) : (
    <span className="hl-hero-flow">
      <span className="hl-plate">Leadify AI continuously monitors social media conversations,</span>{" "}
      <span className="hl-plate">
        finding the <strong>golden opportunities</strong> for you, anywhere and anytime,
      </span>{" "}
      <span className="hl-plate">
        and sending you <strong>real-time</strong> alerts
      </span>
    </span>
  );

/**
 * Shrink the headline until its longest line fits the copy column.
 *
 * The design requires line 1 to stay on ONE line from 1000px up, and a `clamp()`
 * font size alone cannot promise that: the Hebrew string's rendered width
 * depends on the loaded face, so it only becomes knowable after the webfont
 * swaps in. Hence a measure-and-shrink pass, re-run on resize and once
 * `document.fonts.ready` settles.
 *
 * Below 1000px the size is handed back to the stylesheet - the headline is
 * allowed to wrap there.
 *
 * `wordsKey` is the headline's text: when the words change (the homepage's
 * ad-visitor variant swaps them in on the hydrated render, and its line 1 is
 * longer) the new heading has to be measured again, not left at the old size.
 */
function useHeadlineFit(headingRef, columnRef, wordsKey) {
  const fit = useCallback(() => {
    const heading = headingRef.current;
    const column = columnRef.current;
    if (!heading || !column) return;

    heading.style.fontSize = "";
    if (window.innerWidth < WIDE_MIN_PX) return;

    const start = parseFloat(window.getComputedStyle(heading).fontSize);
    // jsdom (and any environment without layout) reports no usable size. Bail
    // rather than write "NaNpx" onto the element.
    if (!Number.isFinite(start) || start <= 0) return;

    const max = column.clientWidth;
    if (!max) return;

    const lines = Array.from(heading.querySelectorAll("[data-hero-line]"));
    let size = start;
    // Bounded: 40 steps of 2px cannot run away even if a measurement never
    // settles.
    for (let step = 0; step < 40; step += 1) {
      const widest = Math.max(0, ...lines.map((line) => line.scrollWidth));
      if (widest <= max) break;
      size -= 2;
      heading.style.fontSize = `${size}px`;
    }
  }, [headingRef, columnRef]);

  useEffect(() => {
    let alive = true;
    const run = () => {
      if (alive) fit();
    };

    run();
    const onResize = rafThrottle(run);
    window.addEventListener("resize", onResize.handler);
    // The webfont swap changes every measurement, so re-fit once it lands.
    if (typeof document !== "undefined" && document.fonts?.ready?.then) {
      document.fonts.ready.then(run).catch(() => {
        /* the resize listener is still the fallback */
      });
    }
    return () => {
      alive = false;
      window.removeEventListener("resize", onResize.handler);
      onResize.cancel();
    };
  }, [fit, wordsKey]);
}

/** One word of the headline, with its arrival index. */
const Word = ({ index, children }) => (
  <Box component="span" className="hl-word" style={{ "--hl-i": index }}>
    {children}
  </Box>
);

/**
 * One headline line: every word its own span, separated by a real space.
 *
 * The lines used to be written out word by word, which pinned them at exactly
 * two words each; a profession page's headline is not that shape. The mapping
 * emits the SAME children a hand-written line did - span, " ", span - so the
 * default headline's DOM is unchanged, and `startIndex` keeps the arrival
 * order running across both lines (0,1 then 2,3 on the homepage).
 */
const HeadlineWords = ({ words, startIndex = 0 }) =>
  words.flatMap((word, i) => {
    const node = (
      <Word key={startIndex + i} index={startIndex + i}>
        {word}
      </Word>
    );
    return i === 0 ? [node] : [" ", node];
  });

const btnBaseSx = {
  position: "relative",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "10px",
  height: 54,
  px: "30px",
  borderRadius: "999px",
  fontWeight: 700,
  fontSize: "16px",
  fontFamily: "var(--hl-font)",
  textDecoration: "none",
  overflow: "hidden",
  transition: "transform .35s var(--hl-ease), box-shadow .35s var(--hl-ease)",
  // The phone size (owner round 4, "reduce ... the two buttons' sizes"),
  // alongside the H1 and the sub/body drop in homepage.css. Spread into BOTH
  // buttons - the secondary is `{...btnBaseSx}` - so the pair stays a pair.
  // 44px is the floor, not a chosen number: it is the tap-target minimum, so
  // the box shrinks to exactly it and no further, and there is no separate
  // hit-area pseudo-element to maintain the way the nav's 38px buttons need.
  [MOBILE]: { height: 44, px: "20px", fontSize: "14px" },
};

// ONE STEP UP from the pair's shared box, for a page whose hero carries more
// copy around the buttons than the homepage's does (owner, 2026-09-17: the
// /lawyers CTA "looks smaller" - measured, both pages render the identical
// 133x54 pill, so the fix is a real size step, not a repair). Opt-in via
// `ctaSize="large"`, so the homepage - which passes nothing - is untouched.
// The phone keeps 44px: that is the tap-target floor, not a chosen number.
const largeCtaSx = {
  height: 60,
  px: "36px",
  fontSize: "17px",
  [MOBILE]: { height: 46, px: "22px", fontSize: "15px" },
};

// STATIC sx OBJECTS, hoisted. An inline `sx={{...}}` is a fresh object on
// every render, and for a memoised child (LeadCardLive, below) a fresh object
// is a changed prop: the memo never hit and the card re-rendered with the
// hero. Module constants are the same reference forever.
const secondaryBtnSx = {
  ...btnBaseSx,
  color: "var(--hl-ink)",
  border: "1px solid var(--hl-hair-2)",
  background: "rgba(0,90,82,.03)",
  "&:hover": {
    borderColor: "var(--hl-teal)",
    background: "rgba(107,170,168,.1)",
  },
};

const microSx = {
  mt: "14px",
  fontSize: "13px",
  color: "var(--hl-ink-3)",
  fontFamily: "var(--hl-mono)",
  letterSpacing: ".02em",
  // 11.5px: the line is 300px wide at 13px and a 360 phone's column
  // is 280.
  [MOBILE]: { mt: "10px", fontSize: "11.5px" },
};

// THE AD LAYOUT (`layout="ad"`, the homepage's ad-visitor hero only - owner-
// approved 2026-10-19). Its phone rules key on 600px, not on the hero's 900:
// the approved spacing was set for a phone held upright, and a 600-900 tablet
// keeps the shared phone scale.
const AD_PHONE = "@media (max-width:600px)";

// The headline: on a phone it scales with the viewport instead of the shared
// clamp, and stays two lines from 320 to 600px. Above 600 it is untouched.
// 9.4vw is the approved size; it only fits the column (the viewport less the
// 80px of gutters) down to ~352px, because the ad's longer line ("יש דרך חכמה
// יותר.") measures 8.36em, 8.22em net of the plate's .14em start bleed. So
// below that the size follows the column instead - 8.25 is that measure with a
// hair of slack - and the lines never wrap, which keeps the headline two lines
// even when a device's Heebo renders a pixel wider than measured. Measured in
// Chrome at 320/340/360/390; new ad words would need the divisor re-measured.
// The 44px ceiling only bites from ~468px up (9.4vw = 44px there), so phones
// up to 430 keep the approved size while a 560-600px window no longer grows a
// 56px headline.
const adHeadingSx = {
  [AD_PHONE]: {
    fontSize: "min(9.4vw, 44px, calc((100vw - 80px) / 8.25))",
    lineHeight: 1.15,
    "& [data-hero-line]": { whiteSpace: "nowrap" },
  },
};

// The sub and the body sit straight on the background - no `hl-plate` on them
// (HeroLive drops the class), and the phone body's negative inline margin,
// which only ever existed to bleed that plate into the gutter, is handed back.
// The body takes the sub's measure (the column) instead of `52ch`, so its last
// word never drops onto a line of its own. The selectors are scoped under the
// copy column's own class, so they beat homepage.css's single-class rules.
const adColumnSx = {
  "& .hl-hero-body": { maxWidth: "none", marginInline: 0, textWrap: "pretty" },
  [AD_PHONE]: {
    "& .hl-hero-sub": { marginTop: "26px", fontSize: "17px", lineHeight: 1.8 },
    "& .hl-hero-body": { marginTop: "20px", fontSize: "16px", lineHeight: 1.8 },
  },
};

// The optional tick list (the homepage's ad-visitor hero only - every other
// hero passes none and renders nothing). Plain inline items, no chips: a small
// teal check BEFORE each line (on its right in RTL, so it reads as that line's
// mark and not the previous one's) and a semibold line in the hero's ink. It
// sits UNDER the button: one row from the text's start edge on a wide screen;
// on a phone two centred rows - the first two items, then the third alone.
const ticksSx = {
  listStyle: "none",
  m: 0,
  mt: "22px",
  p: 0,
  display: "flex",
  flexWrap: "wrap",
  justifyContent: "flex-start",
  columnGap: "18px",
  rowGap: "8px",
  [AD_PHONE]: {
    mt: "38px",
    justifyContent: "center",
    rowGap: "12px",
    // The third item takes a row of its own, its mark and words centred in it.
    "& > li:nth-of-type(3)": { flexBasis: "100%", justifyContent: "center" },
  },
};

const tickSx = {
  display: "inline-flex",
  alignItems: "center",
  gap: "6px",
  fontFamily: "var(--hl-font)",
  fontSize: "14px",
  fontWeight: 600,
  lineHeight: 1.4,
  color: "var(--hl-ink)",
  whiteSpace: "nowrap",
  "& svg": { flex: "none", color: "var(--hl-teal)" },
  [MOBILE]: { fontSize: "13.5px" },
};

// The button row. The ad layout carries ONE button, sized by its label (the
// row is a flex row, so the anchor never stretches); on a phone it sits
// further from the body and centred, over the centred ticks. A wide screen
// keeps it at the text's start edge.
const ctaRowSx = {
  display: "flex",
  gap: "14px",
  mt: "36px",
  flexWrap: "wrap",
  [MOBILE]: { mt: "26px" },
};
const adCtaRowSx = { ...ctaRowSx, [AD_PHONE]: { mt: "34px", justifyContent: "center" } };

/** The decorative check in front of each tick (the text carries the meaning). */
const TickIcon = () => (
  <svg width="15" height="15" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
    <path
      d="M3.2 8.6l3.1 3.1 6.5-7.2"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// The optional reassurance line (profession pages only - the homepage passes
// none, so nothing renders and its DOM is untouched). It is the hero's own
// register, not a new one: the sub's face and the micro line's ink, wearing
// `hl-plate` for the same reason every other hero line does - the mesh runs
// straight under it. No new stylesheet rule and no keyframe: `hl-anim-in`
// carries the entrance and stops with everything else when motion is off.
const reassuranceSx = {
  mt: "12px",
  fontSize: "14px",
  lineHeight: 1.65,
  color: "var(--hl-ink-3)",
  fontFamily: "var(--hl-font)",
  maxWidth: "52ch",
  [MOBILE]: { mt: "10px", fontSize: "12.5px" },
};

const statsRowSx = {
  display: "flex",
  mt: "44px",
  pt: "26px",
  borderTop: "1px solid var(--hl-hair)",
  // The 18px bleed is what buys the posts label its single line: the hero
  // copy column is the wrap's own 280px at 360, and three stat columns of 93px
  // could not hold 19 Hebrew characters plus a chip. Phones only - below 900
  // the hero is one column, so there is a gutter to bleed into.
  // Phones: the three columns size to their CONTENT instead of splitting the
  // row in equal thirds. "500,000+ / פוסטים נסרקים בחודש" needs about twice
  // the measure of "24/7 / ניטור רציף", and equal thirds gave the cramped
  // column its neighbours' spare room to look at. `auto` rather than
  // `max-content`: an auto track sizes to content when the room is there and
  // SHRINKS when it is not, so a font that renders wider than measured costs
  // a wrapped label, never a horizontally scrolling page.
  [MOBILE]: {
    mt: "28px",
    pt: "18px",
    marginInline: "-18px",
    display: "grid",
    gridTemplateColumns: "auto auto auto",
    // PACKED to the row's start (the right, in RTL) with an even gap, not
    // spread edge to edge: `space-between` put the leftover width BETWEEN the
    // tiles, which broke them into three separate things instead of one group.
    // The remainder now sits at the end of the row, where it reads as margin.
    justifyContent: "start",
    columnGap: "26px",
  },
};
// The ad layout's phone keeps 36px clear between its ticks and the strip.
const adStatsRowSx = { ...statsRowSx, [AD_PHONE]: { mt: "36px" } };

const statSx = {
  flex: 1,
  // Without this a flex item refuses to shrink below its
  // min-content width, which on a 360px phone is enough to push
  // the third stat off the edge.
  minWidth: 0,
  position: "relative",
  paddingInlineStart: { xs: "10px", sm: "18px" },
  "&::before": {
    content: '""',
    position: "absolute",
    insetInlineStart: 0,
    top: "6px",
    bottom: "6px",
    width: "1px",
    background:
      "linear-gradient(to bottom, transparent, var(--hl-teal), transparent)",
    // The divider used to fade in with the row, which carried `.hl-anim-in`.
    // The row cannot carry it any more (see the chip note on the label), so
    // the rule moves here and the entrance is unchanged. The keyframe itself
    // stays in homepage.css - nothing declares one inline.
    opacity: 0,
    animation: "hl-fadeUp 1s var(--hl-ease) var(--hl-d, 0s) forwards",
  },
  [MOBILE]: {
    // Each tile centres its figure and label between symmetric 8px paddings,
    // so a figure never hugs the divider on its start side - the hero copy
    // is start-aligned and the tiles used to inherit that.
    paddingInline: "8px",
    textAlign: "center",
    // Half the 26px column gap, so each divider sits in the MIDDLE of its gap
    // rather than hugging the column it belongs to.
    "&::before": { insetInlineStart: "-13px" },
    // ...and the first tile has no gap before it to sit in. On the phone the
    // row is bled to the screen edge, so that divider would have hung 7px off
    // the edge of the viewport. Desktop keeps every divider it has today.
    "&:first-of-type::before": { display: "none" },
  },
};

const statLabelSx = {
  fontSize: "12.5px",
  // 400 -> 500 for legibility over the mesh (Heebo is variable 300-900, so
  // this is a real weight, not a synthesised one). The colour is unchanged.
  fontWeight: 500,
  color: "var(--hl-ink-3)",
  // `.hl-plate` bleeds .38em into the start gutter so a hero line's glass sits
  // under the text edge; a stat label must stay aligned with the figure above
  // it instead, so the bleed is zeroed here and the chip grows outward only.
  marginInlineStart: 0,
  // The phone squeeze that used to live here - 10px, -.02em tracking and a
  // trimmed chip - is GONE: content-sized columns gave the posts label the
  // room it was missing, so it is back to the row's own 11px at normal
  // tracking, wearing the plate's full padding like its two neighbours.
  [MOBILE]: { fontSize: "11px" },
};

const stageSx = {
  position: "relative",
  minWidth: 0,
  perspective: "1400px",
  perspectiveOrigin: "60% 40%",
  height: "auto",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "16px",
  [WIDE]: { display: "block", height: "520px" },
};

const orbitSx = {
  display: "none",
  [WIDE]: { display: "block" },
  position: "absolute",
  inset: "-10% -5%",
  borderRadius: "50%",
  filter: "blur(30px)",
  background:
    "radial-gradient(closest-side, rgba(107,170,168,.18), transparent 70%)",
};

const leadCardSx = {
  position: "relative",
  width: "100%",
  maxWidth: "430px",
  [WIDE]: {
    position: "absolute",
    left: "50%",
    top: "46%",
    transform: "translate(-50%, -50%)",
    width: "min(430px, 94%)",
  },
};

const trayWrapSx = {
  width: "100%",
  display: "flex",
  justifyContent: "center",
  [WIDE]: {
    position: "absolute",
    insetInlineStart: 0,
    bottom: "-6px",
    width: "auto",
  },
};

const gateSx = { position: "absolute", inset: 0, pointerEvents: "none" };

const scrollHintSx = {
  display: { xs: "none", md: "flex" },
  position: "absolute",
  bottom: "26px",
  left: "50%",
  transform: "translateX(-50%)",
  flexDirection: "column",
  alignItems: "center",
  gap: "10px",
  fontFamily: "var(--hl-mono)",
  fontSize: "10.5px",
  letterSpacing: ".3em",
  color: "var(--hl-ink-3)",
};

/**
 * The hero.
 *
 * EVERY COPY PROP IS OPTIONAL AND DEFAULTS TO TODAY'S CONSTANT, which is the
 * load-bearing rule of the profession-landing work: `Homepage.js` passes none
 * of them, so the homepage renders exactly what it rendered before the props
 * existed. A profession page passes its own words and gets the same hero.
 *
 * @param {object} props
 * @param {boolean} [props.isHebrew]
 * @param {string[]} [props.line1] headline line 1, one entry per word
 * @param {string[]} [props.line2] headline line 2, one entry per word
 * @param {string} [props.sub] the tagline under the headline
 * @param {React.ReactNode} [props.body] the plate paragraph under the sub.
 *   Absent (undefined) keeps the homepage's three plates; an explicit `null`
 *   renders NO paragraph at all - a page whose approved copy has none must not
 *   be left with an empty glass chip
 * @param {"default"|"large"} [props.ctaSize] "large" steps BOTH hero buttons
 *   up one size; the homepage passes nothing and keeps today's box
 * @param {string} [props.primaryCta] label of the /register button
 * @param {string} [props.secondaryCta] label of the /login button
 * @param {string} [props.micro] the small line under the buttons
 * @param {Array<{n: string, l: string}>} [props.stats] the three stat chips
 * @param {string} [props.reassurance] one quiet line under the micro line;
 *   renders NOTHING when absent, which is what the homepage does
 * @param {string[]} [props.ticks] short selling points rendered as a check
 *   list under the buttons; renders NOTHING when absent
 * @param {"default"|"ad"} [props.layout] "ad" is the homepage's ad-visitor
 *   arrangement: no glass behind the sub and the body, ONE button (no /login,
 *   no micro line) and its own phone type scale and spacing. Everything else
 *   passes nothing and renders exactly as before
 * @param {Array} [props.leads] the card deck (stage, cycle and phone strip)
 * @param {Array} [props.fields] the mesh's profession tag deck
 */
export default function HeroLive({
  isHebrew = true,
  line1,
  line2,
  sub,
  body,
  primaryCta,
  secondaryCta,
  micro,
  stats,
  ctaSize = "default",
  reassurance,
  ticks,
  layout = "default",
  leads = LEADS,
  fields = FIELDS,
}) {
  const t = isHebrew ? COPY.he : COPY.en;
  const ad = layout === "ad";
  const copy = {
    line1: line1 ?? t.line1,
    line2: line2 ?? t.line2,
    sub: sub ?? t.sub,
    primaryCta: primaryCta ?? t.primaryCta,
    secondaryCta: secondaryCta ?? t.secondaryCta,
    micro: micro ?? t.micro,
    stats: stats ?? t.stats,
  };
  // `undefined` means "unset" and takes the homepage's plates; an explicit
  // `null` means "this page has no body paragraph" and drops the element.
  const bodyContent = body === undefined ? <HeroBody isHebrew={isHebrew} /> : body;
  // The glass under the sub and the body. The ad layout's text sits straight
  // on the background.
  const plateClass = ad ? "" : " hl-plate";
  // The card the page opens on, and the one a still page (motion off, the
  // prerenderer, the frame before the cycle starts) keeps showing.
  // The pair grows together or not at all - a 60px primary beside a 54px
  // secondary reads as a mistake, not as hierarchy.
  const ctaSx = ctaSize === "large" ? { ...btnBaseSx, ...largeCtaSx } : btnBaseSx;
  const featured = leads[0] ?? LEADS[0];
  const headingRef = useRef(null);
  const columnRef = useRef(null);
  // The headline's identity. It keys the <h1> so a change of words (the
  // homepage's ad-visitor variant, swapped in on the hydrated render) mounts
  // fresh word spans and their entrance replays, and it re-runs the fit.
  // Constant on every page whose copy never changes, so nothing remounts there.
  const headlineKey = `${copy.line1.join(" ")}|${copy.line2.join(" ")}`;
  useHeadlineFit(headingRef, columnRef, headlineKey);

  const { off: motionOff, paused } = useMotion();
  const [gateRef, heroInView] = useInViewGate({ threshold: 0.2 });
  const prerendering = isPrerendering();
  // The prerendered snapshot carries the finished card; a hydrating client
  // must render the same figure on its first pass (the cycle then starts from
  // a fill, exactly as on any other boot).
  const hydrated = useHydrated();

  const stageRef = useRef(null);
  const frontRef = useRef(null);
  const cardRef = useRef(null);
  const burstTimerRef = useRef(null);

  // A phone gets the card without the flight: the entrance is a full-screen
  // transform on a device where it competes with the scroll, and the stage has
  // no empty page beside it for the node to ignite in anyway.
  const [mobile, setMobile] = useState(isMobileWidth);
  // ...and since 2026-09-05 it gets the whole DECK instead, as a swipeable
  // strip (HeroCarouselLive). `hydrated` is what keeps that off the prerendered
  // snapshot AND off the hydrating client's first pass: puppeteer snapshots the
  // page at 800px, which is under this breakpoint, so branching the MARKUP on
  // width alone would bake the strip into the HTML every desktop visitor then
  // hydrates against. The snapshot carries the desktop figure at every width
  // and the phone swaps one render later, before paint (motion/useHydrated.js).
  const phone = hydrated && !prerendering && mobile;
  useEffect(() => {
    const onResize = rafThrottle(() => setMobile(isMobileWidth()));
    window.addEventListener("resize", onResize.handler);
    return () => {
      window.removeEventListener("resize", onResize.handler);
      onResize.cancel();
    };
  }, []);

  // The cycle runs only when there is someone to watch it and motion is
  // welcome. Under the prerenderer it never starts, and the initial state below
  // is what the snapshot ships. `paused` covers the hidden tab: the cycle's
  // timers would otherwise keep swapping leads in the background, and a tab
  // that comes back simply restarts from a fill.
  const cycleEnabled = !motionOff && !paused && !prerendering && heroInView;

  // CONTENT state. The initial values are the cycle's END state, not its start:
  // a still page (motion off, prerender, or the moment before the first frame)
  // must show a FINISHED card, never an empty one waiting for an animation that
  // is not coming.
  const [lead, setLead] = useState(featured);
  const [hot, setHot] = useState(() => featured.score >= HERO_HOT_SCORE);
  const [burst, setBurst] = useState(null);

  // THE SCORE IS NOT REACT STATE (perf batch, 2026-09-04). The count-up hands
  // a value per frame for 1.2s, and routing that through setState re-rendered
  // the whole hero ~70 times per card. The card is rendered ONCE with the
  // initial score - the finished value on a still page, 0 when the cycle is
  // about to run - and from then on the count writes the two nodes that show
  // it (the figure and the bar) directly through refs. React never renders a
  // different `score`, so it never touches those nodes again.
  const initialScoreRef = useRef(
    getMotionState().off || isPrerendering() || !hydrated ? featured.score : 0
  );
  const scoreRef = useRef(null);
  const barRef = useRef(null);
  const leadRef = useRef(featured);
  const writeScore = useCallback((value) => {
    if (scoreRef.current) scoreRef.current.textContent = `${value}%`;
    // The prototype sets the bar to the FINAL score as the count starts and
    // lets its 1.4s ease carry it there while the number counts up over 1.2s
    // - so the bar leads the number, on purpose. The first counted value is
    // the "count has started" signal; a reset (0) drops the bar back to 0.
    if (barRef.current) barRef.current.style.width = `${value > 0 ? leadRef.current.score : 0}%`;
  }, []);
  const setLeadTracked = useCallback((next) => {
    leadRef.current = next;
    setLead(next);
  }, []);

  // With motion off the card never animates, so it must be handed its end state
  // directly - including when the toggle is flipped mid-visit.
  useEffect(() => {
    if (!motionOff) return;
    writeScore(lead.score);
    setHot(lead.score >= HERO_HOT_SCORE);
    if (cardRef.current) cardRef.current.style.opacity = "1";
  }, [motionOff, lead, writeScore]);

  // Hide the card BEFORE the first paint when the cycle is going to run, so the
  // page never shows an un-scored card for a frame and then snatches it away.
  // The prototype gets this free - its script runs before the first paint - and
  // a plain effect does not: effects run after it. Only when the cycle IS going
  // to run: a hidden tab (`paused`) mounts with no cycle and nothing to reveal
  // the card, so it must stay visible for the moment the tab comes back
  // (final review #10).
  useLayoutEffect(() => {
    if (motionOff || prerendering || paused) return;
    if (cardRef.current) cardRef.current.style.opacity = "0";
    // Mount only: from here the cycle owns this property.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const showBurst = useCallback((point) => {
    setBurst({ ...point, key: `${point.x}:${point.y}:${Date.now()}` });
    clearTimeout(burstTimerRef.current);
    burstTimerRef.current = setTimeout(() => setBurst(null), BURST_MS);
  }, []);
  useEffect(() => () => clearTimeout(burstTimerRef.current), []);

  const cycleRefs = useMemo(
    () => ({ front: frontRef, card: cardRef, stage: stageRef }),
    []
  );
  const cycleActions = useMemo(
    () => ({ setLead: setLeadTracked, setScore: writeScore, setHot, showBurst }),
    [setLeadTracked, writeScore, showBurst]
  );

  useHeroCycle({
    // NEVER on a phone: the strip owns the deck there, and a cycle running
    // beside it would swap the lead out from under the visitor's finger, ask
    // the mesh to ignite nodes for a card that is not mounted, and count a
    // score into refs the carousel's own cards do not carry.
    enabled: cycleEnabled && !phone,
    reduced: mobile,
    leads,
    hotScore: HERO_HOT_SCORE,
    refs: cycleRefs,
    actions: cycleActions,
  });

  return (
    <SectionShell
      // `id="hero"` also names the section for the engagement tracker
      // (SectionShell derives data-lf-section from an allowlisted id), on
      // every hero - homepage and profession pages alike.
      id="hero"
      scene="hero"
      mesh="cloud"
      meshAlpha="1"
      sx={{
        minHeight: { md: "100vh" },
        display: "grid",
        alignItems: "center",
        pt: { xs: "112px", md: "150px" },
        pb: { xs: "56px", md: "80px" },
      }}
      innerSx={{
        display: "grid",
        gridTemplateColumns: "1fr",
        gap: "56px",
        alignItems: "center",
        [WIDE]: { gridTemplateColumns: "1.15fr .85fr", gap: "72px" },
      }}
    >
      {/* ---------------------------------------------------------------- copy */}
      {/* `hl-hero-copy` is what the homepage's ad-hero hold hides before
          hydration (homepage.css); inert everywhere else. */}
      <Box
        ref={columnRef}
        className="hl-hero-copy"
        sx={{
          minWidth: 0,
          position: "relative",
          zIndex: 2,
          ...(ad ? adColumnSx : null),
        }}
      >
        <Typography
          key={headlineKey}
          component="h1"
          ref={headingRef}
          sx={{
            fontFamily: "var(--hl-font)",
            fontSize: "clamp(38px, 4.3vw, 66px)",
            // Owner round 3, "reduce even more": 28.1px at 390, 26px at 360.
            // Round 4, "a little more": 25.7px at 390, 24px at 360 (the 24px
            // floor holds from 364px down). Line 1 ("מקסימום הזדמנויות.")
            // measures 344px at 38px - 9.05em - so it is 233px at 390 and
            // 217px at 360, still well inside the 310px / 280px column: the
            // slogan stays two lines on a phone, never three.
            [MOBILE]: { fontSize: "clamp(24px, 6.6vw, 30px)" },
            // 900 -> 700 with every section heading (owner, 2026-10).
            fontWeight: 700,
            lineHeight: 1.05,
            letterSpacing: 0,
            color: "var(--hl-ink)",
            m: 0,
            ...(ad ? adHeadingSx : null),
          }}
        >
          <Box
            component="span"
            data-hero-line
            sx={{ display: "block", [WIDE]: { whiteSpace: "nowrap" } }}
          >
            <Box component="span" className="hl-plate hl-plate-h1">
              <HeadlineWords words={copy.line1} startIndex={0} />
            </Box>
          </Box>
          <Box
            component="span"
            data-hero-line
            sx={{
              display: "block",
              position: "relative",
              [WIDE]: { whiteSpace: "nowrap" },
              "& .hl-word": { color: "var(--hl-teal)" },
            }}
          >
            <Box component="span" className="hl-plate hl-plate-h1">
              <HeadlineWords words={copy.line2} startIndex={copy.line1.length} />
            </Box>
            <Box component="i" className="hl-under" aria-hidden="true" />
          </Box>
        </Typography>

        {/* Plain <p> elements, styled by `.hl-hero-sub` / `.hl-hero-body` in
            homepage.css, like every other section. They were MUI Typography
            until the review-A fidelity pass: Typography's emotion class beat
            `.hl-root`'s inheritance, so the sub rendered in DM Sans at a 39px
            line-height with MUI's letter-spacing, `52ch` resolved against the
            wrong face, and Typography's `margin: 0` wiped the plate's
            -.38em bleed. */}
        <p className={`hl-hero-sub hl-anim-in${plateClass}`} style={{ "--hl-d": "0.9s" }}>
          {copy.sub}
        </p>

        {/* The body carries the plate ON THE PARAGRAPH, exactly as the sub
            above does, and for a reason that is not style: `.hl-anim-in`
            animates opacity, which makes the element a BACKDROP ROOT, and a
            `backdrop-filter` on a DESCENDANT of one samples an empty backdrop.
            The clause spans inside were plates for months and blurred nothing -
            only their flat 12% white ever landed, so the mesh cut straight
            through the copy while the sub's identical plate blurred it (owner,
            2026-09-06). The glass has to sit on the animated element itself,
            so it does; the clauses keep their SHAPE and give up their glass in
            homepage.css. */}
        {bodyContent ? (
          <p className={`hl-hero-body hl-anim-in${plateClass}`} style={{ "--hl-d": "1.05s" }}>
            {bodyContent}
          </p>
        ) : null}

        <Box className="hl-anim-in" style={{ "--hl-d": "1.2s" }} sx={ad ? adCtaRowSx : ctaRowSx}>
          <Box
            component={RouterLink}
            to="/register"
            // The sentinel HomeNav watches (HERO_CTA_SELECTOR): the phone pill
            // grows its own register button only once THIS one has scrolled
            // above the viewport, so the two are never on screen together.
            data-hero-cta=""
            sx={{
              ...ctaSx,
              color: "#FFFFFF",
              background: "linear-gradient(135deg,#40E0D0 0%,#00A896 100%)",
              // One hairline lift, the same at rest and on hover (owner,
              // 2026-10: no coloured halo, no hover shine sweep). The arrow
              // nudge below is the button's only hover motion.
              boxShadow: "var(--hl-shadow-btn)",
              "&:hover .hl-arr": {
                transform: isHebrew ? "translateX(-5px)" : "translateX(5px)",
              },
            }}
          >
            {copy.primaryCta}
            <Arrow className="hl-arr" direction={isHebrew ? "left" : "right"} />
          </Box>
          {ad ? null : (
            <Box
              component={RouterLink}
              to="/login"
              sx={ctaSize === "large" ? { ...secondaryBtnSx, ...largeCtaSx } : secondaryBtnSx}
            >
              {copy.secondaryCta}
            </Box>
          )}
        </Box>

        {/* The ad layout has no micro line; its ticks take that slot - and its
            1.35s entrance - under the button. */}
        {ad ? null : (
          <Box className="hl-anim-in" style={{ "--hl-d": "1.35s" }} sx={microSx}>
            {copy.micro}
          </Box>
        )}

        {ticks && ticks.length ? (
          // role="list": `list-style: none` makes Safari's VoiceOver drop the
          // list semantics of a plain <ul>; the explicit role keeps them.
          <Box
            component="ul"
            role="list"
            className="hl-anim-in hl-hero-ticks"
            style={{ "--hl-d": "1.35s" }}
            sx={ticksSx}
          >
            {ticks.map((tick) => (
              <Box component="li" key={tick} sx={tickSx}>
                <TickIcon />
                <span>{tick}</span>
              </Box>
            ))}
          </Box>
        ) : null}

        {/* The reassurance line, when a page has one. Absent on the homepage,
            so this renders nothing there. */}
        {reassurance ? (
          <Box
            className="hl-anim-in hl-plate"
            style={{ "--hl-d": "1.42s" }}
            sx={reassuranceSx}
          >
            {reassurance}
          </Box>
        ) : null}

        {/* THE ROW IS NO LONGER THE ANIMATED ELEMENT, and that is a chip
            requirement rather than a style choice: `.hl-anim-in` animates
            opacity, which makes an element a BACKDROP ROOT, and a
            `backdrop-filter` on a DESCENDANT of one has nothing to sample - a
            label chip inside an animated row would have been a flat wash with
            no blur (the same trap that had been sitting under the hero's own
            body copy). So the entrance moves onto the three figures, the three
            labels and the dividers, each keeping the row's 1.5s delay through
            the inherited `--hl-d`; the label then carries its glass on the
            very element that animates, exactly as `.hl-hero-sub` does. */}
        <Box style={{ "--hl-d": "1.5s" }} sx={ad ? adStatsRowSx : statsRowSx}>
          {copy.stats.map((stat) => (
            <Box key={stat.l} sx={statSx}>
              <Box
                className="hl-anim-in"
                component="b"
                sx={{
                  display: "block",
                  fontFamily: "var(--hl-mono)",
                  fontSize: "clamp(22px, 2.4vw, 30px)",
                  // "500,000+" is EIGHT mono glyphs (the old "20,000+" was
                  // seven), and a 360 phone's stat column is 93px INCLUDING its
                  // 10px inset - 83px of content, where eight glyphs measure 87.
                  // The owner's clamp stays; the extra -.03em buys the 4px, the
                  // typesNumbersCta.css precedent for the same figure.
                  [MOBILE]: {
                    fontSize: "clamp(18px, 5.2vw, 22px)",
                    letterSpacing: "-.05em",
                  },
                  fontWeight: 600,
                  color: "var(--hl-ink)",
                  letterSpacing: "-.02em",
                  // The figures are Latin/ASCII: read them LTR even on an RTL
                  // page, but keep them aligned to the column's own edge.
                  direction: "ltr",
                  textAlign: isHebrew ? "right" : "left",
                }}
              >
                {stat.n}
              </Box>
              {/* `hl-plate` is the hero's own chip idiom, and it is NOT
                  phone-only: it is 12% white over a 7px blur on a desktop and
                  28% over 16px + saturation below 900px, which is exactly what
                  the tagline and the body copy already wear. One chip per
                  label, shrink-wrapped by the plate's `display: table`. */}
              <Box component="span" className="hl-anim-in hl-plate" sx={statLabelSx}>
                {stat.l}
              </Box>
            </Box>
          ))}
        </Box>
      </Box>

      {/* --------------------------------------------------------------- stage */}
      {/* On xs the card and the tray are normal flow items. The prototype keeps
          the card absolutely centred at every width, which overflows a 360px
          phone and drops the tray on top of it.

          `perspective` here is what gives the card its depth as it flies in -
          and it is also why the docked tray has to be portalled out: perspective
          makes this box a containing block for `position: fixed`. */}
      <Box ref={stageRef} sx={stageSx}>
        <Box className="hl-orbit" aria-hidden="true" sx={orbitSx} />

        {phone ? (
          <HeroCarouselLive
            leads={leads}
            hotScore={HERO_HOT_SCORE}
            scoreLabel={t.scoreLabel}
            hotStamp={t.hotStamp}
            isHebrew={isHebrew}
            enabled={cycleEnabled}
            motionOff={motionOff}
          />
        ) : (
          <LeadCardLive
            ref={frontRef}
            cardRef={cardRef}
            scoreRef={scoreRef}
            barRef={barRef}
            lead={lead}
            score={initialScoreRef.current}
            hot={hot}
            scoreLabel={t.scoreLabel}
            hotStamp={t.hotStamp}
            sx={leadCardSx}
          />
        )}

        <Box sx={trayWrapSx}>
          <ScannedTray
            label={t.trayLabel}
            motionOff={motionOff}
            staticCount={prerendering}
            dir={isHebrew ? "rtl" : "ltr"}
          />
        </Box>
      </Box>

      {/* The viewport gate. A sentinel spanning the section rather than a ref on
          any one element: the cycle, the ignite tags and the mesh all ask "is
          the hero on screen", and none of them should be coupled to whichever
          box happens to be the tallest. */}
      <Box ref={gateRef} aria-hidden="true" sx={gateSx} />

      {/* The ring the card grows out of. Fixed, and OUTSIDE the stage - the
          stage's `perspective` would trap it. */}
      {burst && (
        <i
          key={burst.key}
          className="hl-node-burst"
          aria-hidden="true"
          style={{ left: `${burst.x}px`, top: `${burst.y}px` }}
        />
      )}

      <IgniteTag
        enabled={!motionOff && !prerendering && !mobile && heroInView}
        fields={fields}
        stageRef={cardRef}
      />

      {/* --------------------------------------------------------- scroll hint */}
      {/* Absolutely positioned, so it leaves the grid flow rather than becoming
          a third column. Hidden on phones, where the fold is obvious anyway. */}
      <Box aria-hidden="true" sx={scrollHintSx}>
        <span>{t.scroll}</span>
        <Box component="i" className="hl-scroll-line" />
      </Box>
    </SectionShell>
  );
}
